import { call_api } from "@approck_web/app_socket.mts";
import {
    type JsonValue,
    type Option,
    Option_decode,
    Option_encode,
    type Result,
    string_decode,
    string_encode,
    type Uuid,
    Uuid_decode,
    Uuid_encode,
} from "@artyp/lib.mts";

export namespace initiate_reset {
    // InnerAdt::NamedStruct: Input
    export type Input = { email: Option<string>; };

    // InnerAdt::NamedStruct: Input
    export function Input_encode(_value: Input): JsonValue {
        return {
            email: ((v) => {
                return Option_encode(v, string_encode);
            })(_value["email"]),
        };
    }

    // InnerAdt::Enum: Output
    export type Output =
        | {
            Ok: {
                reset_password_uuid: Uuid;
                from_address: string;
                from_name: Option<string>;
                subject: string;
            };
        }
        | { "Disabled": true; }
        | { Error: { message: string; }; };

    // Adt::Enum: Output
    export function Output_decode(_data: JsonValue): Result<Output, string> {
        if (typeof _data === "object" && _data !== null && !Array.isArray(_data)) {
            if (
                "Ok" in _data && typeof _data["Ok"] === "object" && _data["Ok"] !== null
                && !Array.isArray(_data["Ok"])
            ) {
                const _obj = _data["Ok"];
                const reset_password_uuid = Uuid_decode(_obj["reset_password_uuid"] ?? null);
                if ("Err" in reset_password_uuid) return reset_password_uuid;
                const from_address = string_decode(_obj["from_address"] ?? null);
                if ("Err" in from_address) return from_address;
                const from_name = ((v) => {
                    return Option_decode(v, string_decode);
                })(_obj["from_name"] ?? null);
                if ("Err" in from_name) return from_name;
                const subject = string_decode(_obj["subject"] ?? null);
                if ("Err" in subject) return subject;
                return {
                    Ok: {
                        "Ok": {
                            "reset_password_uuid": reset_password_uuid.Ok,
                            "from_address": from_address.Ok,
                            "from_name": from_name.Ok,
                            "subject": subject.Ok,
                        },
                    },
                };
            }
            if ("Disabled" in _data) return { Ok: { "Disabled": true } };
            if (
                "Error" in _data && typeof _data["Error"] === "object" && _data["Error"] !== null
                && !Array.isArray(_data["Error"])
            ) {
                const _obj = _data["Error"];
                const message = string_decode(_obj["message"] ?? null);
                if ("Err" in message) return message;
                return { Ok: { "Error": { "message": message.Ok } } };
            }
        }
        return { Err: "invalid variant" };
    }

    // api call function for `initiate_reset`
    export const api = {
        api: "::web::auth::reset_password::initiate_reset",
        call: call,
        Input_encode: Input_encode,
        Output_decode: Output_decode,
    };

    export async function call(data: Input): Promise<Output> {
        const { promise } = call_api(
            "::web::auth::reset_password::initiate_reset",
            Input_encode(data),
        );
        const output = await promise;
        const r = Output_decode(output);
        if ("Err" in r) throw r.Err;
        return r.Ok;
    }
}
export namespace verify_code {
    // InnerAdt::NamedStruct: Input
    export type Input = { reset_password_uuid: Uuid; code: Option<string>; };

    // InnerAdt::NamedStruct: Input
    export function Input_encode(_value: Input): JsonValue {
        return {
            reset_password_uuid: Uuid_encode(_value["reset_password_uuid"]),
            code: ((v) => {
                return Option_encode(v, string_encode);
            })(_value["code"]),
        };
    }

    // InnerAdt::NamedStruct: InputErr
    export type InputErr = { code: Option<string>; };

    // Adt::NamedStruct: InputErr
    export function InputErr_decode(_data: JsonValue): Result<InputErr, string> {
        if (typeof _data === "object" && _data !== null && !Array.isArray(_data)) {
            const r_code = ((v) => {
                return Option_decode(v, string_decode);
            })(_data["code"] ?? null);
            if ("Err" in r_code) return r_code;
            return { Ok: { "code": r_code.Ok } };
        } else return { Err: "Named: wrong type: " + typeof _data };
    }

    // InnerAdt::Enum: Output
    export type Output = { "Ok": true; } | { "Disabled": true; } | { InputErr: [InputErr]; };

    // Adt::Enum: Output
    export function Output_decode(_data: JsonValue): Result<Output, string> {
        if (typeof _data === "object" && _data !== null && !Array.isArray(_data)) {
            if ("Ok" in _data) return { Ok: { "Ok": true } };
            if ("Disabled" in _data) return { Ok: { "Disabled": true } };
            if ("InputErr" in _data && Array.isArray(_data["InputErr"])) {
                const arr = _data["InputErr"];
                const v_0 = InputErr_decode(arr[0] ?? null);
                if ("Err" in v_0) return v_0;
                return { Ok: { "InputErr": [v_0.Ok] } };
            }
        }
        return { Err: "invalid variant" };
    }

    // api call function for `verify_code`
    export const api = {
        api: "::web::auth::reset_password::verify_code",
        call: call,
        Input_encode: Input_encode,
        Output_decode: Output_decode,
    };

    export async function call(data: Input): Promise<Output> {
        const { promise } = call_api(
            "::web::auth::reset_password::verify_code",
            Input_encode(data),
        );
        const output = await promise;
        const r = Output_decode(output);
        if ("Err" in r) throw r.Err;
        return r.Ok;
    }
}
export namespace update_password {
    // InnerAdt::NamedStruct: Input
    export type Input = { reset_password_uuid: Uuid; password: Option<string>; };

    // InnerAdt::NamedStruct: Input
    export function Input_encode(_value: Input): JsonValue {
        return {
            reset_password_uuid: Uuid_encode(_value["reset_password_uuid"]),
            password: ((v) => {
                return Option_encode(v, string_encode);
            })(_value["password"]),
        };
    }

    // InnerAdt::NamedStruct: InputErr
    export type InputErr = { reset_password_uuid: Option<string>; password: Option<string>; };

    // Adt::NamedStruct: InputErr
    export function InputErr_decode(_data: JsonValue): Result<InputErr, string> {
        if (typeof _data === "object" && _data !== null && !Array.isArray(_data)) {
            const r_reset_password_uuid = ((v) => {
                return Option_decode(v, string_decode);
            })(_data["reset_password_uuid"] ?? null);
            if ("Err" in r_reset_password_uuid) return r_reset_password_uuid;
            const r_password = ((v) => {
                return Option_decode(v, string_decode);
            })(_data["password"] ?? null);
            if ("Err" in r_password) return r_password;
            return {
                Ok: { "reset_password_uuid": r_reset_password_uuid.Ok, "password": r_password.Ok },
            };
        } else return { Err: "Named: wrong type: " + typeof _data };
    }

    // InnerAdt::Enum: Output
    export type Output = { "Ok": true; } | { "Disabled": true; } | { InputErr: [InputErr]; };

    // Adt::Enum: Output
    export function Output_decode(_data: JsonValue): Result<Output, string> {
        if (typeof _data === "object" && _data !== null && !Array.isArray(_data)) {
            if ("Ok" in _data) return { Ok: { "Ok": true } };
            if ("Disabled" in _data) return { Ok: { "Disabled": true } };
            if ("InputErr" in _data && Array.isArray(_data["InputErr"])) {
                const arr = _data["InputErr"];
                const v_0 = InputErr_decode(arr[0] ?? null);
                if ("Err" in v_0) return v_0;
                return { Ok: { "InputErr": [v_0.Ok] } };
            }
        }
        return { Err: "invalid variant" };
    }

    // api call function for `update_password`
    export const api = {
        api: "::web::auth::reset_password::update_password",
        call: call,
        Input_encode: Input_encode,
        Output_decode: Output_decode,
    };

    export async function call(data: Input): Promise<Output> {
        const { promise } = call_api(
            "::web::auth::reset_password::update_password",
            Input_encode(data),
        );
        const output = await promise;
        const r = Output_decode(output);
        if ("Err" in r) throw r.Err;
        return r.Ok;
    }
}
